// The public site frame: header with the pages a visitor may open, the server address
// one click away, an account menu once signed in, and the footer. Page visibility comes
// from the site context; the API enforces the same rules whatever the browser shows.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import seoPages from "../../../shared/seo-pages.json";
import { api } from "../api";
import { DiscordMark, Icon } from "../icons";
import { Avatar, Skeleton } from "../ui";
import { areaVisible, useArea, useSite } from "./context";
import { CartButton } from "./store/HeaderStore";
import { useStore } from "./store/StoreSession";

type SiteLink = { to: string; label: string };

export const SERVER_ADDRESS = seoPages.serverAddress;

function useSiteLinks(): SiteLink[] {
  const { me } = useSite();
  const signedIn = Boolean(me.user);
  const join = useArea("public.join"), news = useArea("public.news"), boards = useArea("public.leaderboards"), vote = useArea("public.vote"), community = useArea("public.community"), team = useArea("public.team");
  const store = useStore();
  return [
    areaVisible(join, signedIn) && { to: "/join", label: "Join" },
    areaVisible(news, signedIn) && { to: "/news", label: "News" },
    areaVisible(boards, signedIn) && { to: "/minecraft/islandtop", label: "Leaderboards" },
    store.visible && { to: "/store", label: "Store" },
    areaVisible(vote, signedIn) && { to: "/vote", label: "Vote" },
    areaVisible(community, signedIn) && { to: "/discord/leaderboard", label: "Community" },
    areaVisible(team, signedIn) && { to: "/team", label: "Team" },
  ].filter(Boolean) as SiteLink[];
}

export const loginPath = (returnPath: string) => `/discord/login?returnPath=${encodeURIComponent(returnPath)}`;

// Copies the server address; the label confirms the copy for sighted and screen-reader users alike.
export function CopyAddressButton({ className = "ip-button", compactLabel = true, label = SERVER_ADDRESS }: { className?: string; compactLabel?: boolean; label?: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => { if (!copied) return; const timer = setTimeout(() => setCopied(false), 2000); return () => clearTimeout(timer); }, [copied]);
  const copy = () => navigator.clipboard?.writeText(SERVER_ADDRESS).then(() => setCopied(true)).catch(() => setCopied(false));
  return <button type="button" className={className} onClick={copy} aria-label={copied ? `${SERVER_ADDRESS} copied` : `Copy the server address, ${SERVER_ADDRESS}`}>
    <Icon name={copied ? "check" : "copy"} size={16}/>
    <span className={compactLabel ? "ip-label" : undefined} aria-hidden="true">{copied ? "Copied" : label}</span>
  </button>;
}

export function SiteShell({ children }: { children: ReactNode }) {
  const { me } = useSite();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const links = useSiteLinks();
  const appeal = areaVisible(useArea("public.appeal"), Boolean(me.user));
  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const close = (event: KeyboardEvent | MouseEvent) => { if (event instanceof KeyboardEvent ? event.key === "Escape" : !(event.target as Element | null)?.closest?.(".site-header")) setOpen(false); };
    window.addEventListener("keydown", close);
    document.addEventListener("click", close);
    return () => { window.removeEventListener("keydown", close); document.removeEventListener("click", close); };
  }, [open]);
  const footerLinks: SiteLink[] = [
    { to: "/", label: "Home" },
    ...links,
    ...(appeal ? [{ to: "/appeal", label: "Appeal a ban" }] : []),
    ...(me.permissions.includes("portal.access") ? [{ to: "/discord/portal", label: "Member portal" }] : []),
    ...(me.permissions.includes("staff.access") ? [{ to: "/staff", label: "Staff hub" }] : []),
  ];
  return <div className="site">
    <a className="skip-link" href="#main">Skip to content</a>
    <header className="site-header">
      <div className="container site-header-inner">
        <Link className="site-brand" to="/" aria-label="SkyBoxy MC home"><img src="/assets/skyboxy-wordmark.webp" alt="SkyBoxy MC" width={92} height={40}/></Link>
        <nav id="site-links" className={`primary-nav ${open ? "open" : ""}`} aria-label="Main">
          {links.map((link) => <NavLink key={link.to} to={link.to}>{link.label}</NavLink>)}
        </nav>
        <div className="header-actions">
          <CopyAddressButton/>
          <CartButton/>
          {me.user ? <AccountMenu/> : <Link className="button ghost compact" to={loginPath(location.pathname + location.search)}>Sign in</Link>}
        </div>
        <button className="icon-button site-menu-button" aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open} aria-controls="site-links" onClick={() => setOpen(!open)}><Icon name={open ? "close" : "menu"}/></button>
      </div>
    </header>
    {children}
    <footer className="site-footer">
      <div className="container site-footer-inner">
        <div className="footer-brand">
          <img src="/assets/skyboxy-wordmark.webp" alt="SkyBoxy MC" width={101} height={44}/>
          <p>Run by SkyBlock players, for SkyBlock players. Grab a friend, hop on at <strong>{SERVER_ADDRESS}</strong>, and we will save you an island.</p>
        </div>
        <nav className="footer-links" aria-label="Footer">{footerLinks.map((link) => <Link key={link.to} to={link.to}>{link.label}</Link>)}</nav>
        <div className="footer-legal">
          <span>© {new Date().getFullYear()} SkyBoxy</span>
          <span>SkyBoxy is not affiliated with Mojang Studios or Microsoft.</span>
        </div>
      </div>
    </footer>
  </div>;
}

function AccountMenu() {
  const { me } = useSite();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent | KeyboardEvent) => {
      if (event instanceof KeyboardEvent) { if (event.key === "Escape") { setOpen(false); button.current?.focus(); } return; }
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", close); };
  }, [open]);
  const signOut = () => api("/api/v1/auth/logout", { method: "POST" }).finally(() => location.assign("/"));
  const user = me.user!;
  const can = (permission: string) => me.permissions.includes(permission);
  return <div className="account-menu" ref={ref}>
    <button ref={button} type="button" className="account-button" aria-label={`${user.displayName}, account menu`} aria-expanded={open} aria-haspopup="menu" onClick={() => setOpen(!open)}>
      <Avatar src={user.avatarUrl} name={user.displayName} size={28}/><span className="account-label">{user.displayName}</span><Icon name="chevronDown" size={16}/>
    </button>
    {open && <div className="account-menu-panel" role="menu">
      {can("portal.access") && <Link role="menuitem" to="/discord/portal"><Icon name="portal" size={16}/>Member portal</Link>}
      {can("staff.access") && <Link role="menuitem" to="/staff"><Icon name="shield" size={16}/>Staff hub</Link>}
      {can("dashboard.view") && <Link role="menuitem" to="/discord/admin/overview"><Icon name="overview" size={16}/>Dashboard</Link>}
      <button role="menuitem" type="button" onClick={signOut}><Icon name="logout" size={16}/>Sign out</button>
    </div>}
  </div>;
}

// Wraps a page whose visibility is configurable in Page access.
export function AreaGate({ id, children }: { id: string; children: ReactNode }) {
  const area = useArea(id);
  const { me, loaded } = useSite();
  const location = useLocation();
  // Wait for the access rules so a restricted page never starts loading data it cannot have.
  if (!loaded) return <SiteShell><main id="main" className="container page-head" aria-busy="true"><Skeleton rows={3} label="Loading page"/></main></SiteShell>;
  if (!area || area.allowed) return <>{children}</>;
  const signIn = area.requiresLogin && !me.user;
  return <SiteShell><main className="auth-page" id="main"><section className="auth-card">
    <span className="auth-mark tone-warning"><Icon name="lock" size={24}/></span>
    <h1>{!area.enabled ? "Closed for now" : signIn ? "Sign in to continue" : "Limited to some roles"}</h1>
    <p>{!area.enabled ? "The team has turned this page off for now. Check back later." : signIn ? "This page is open to members with certain Discord roles. Sign in so we can check yours." : "Your Discord roles do not include this page. If you think that is wrong, ask a staff member."}</p>
    <div className="auth-actions">{signIn
      ? <a className="button discord" href={`/api/v1/auth/login?returnPath=${encodeURIComponent(location.pathname + location.search)}`}><DiscordMark/>Sign in with Discord</a>
      : <Link className="button primary" to="/">Back to the home page</Link>}</div>
  </section></main></SiteShell>;
}
